export type IntervalUnit = "day" | "month" | "week" | "year";

export interface BillingInterval {
  count: number;
  interval: IntervalUnit;
}

export interface AppliedCoupon {
  amountOff: null | number;
  currency: null | string;
  duration: "forever" | "once" | "repeating";
  durationInMonths: null | number;
  percentOff: null | number;
}

const INTERVAL_UNITS: IntervalUnit[] = ["day", "week", "month", "year"];
const ADVERBS: Record<IntervalUnit, string> = {
  day: "daily",
  month: "monthly",
  week: "weekly",
  year: "yearly",
};
const SHORT_UNITS: Record<IntervalUnit, string> = {
  day: "day",
  month: "mo",
  week: "wk",
  year: "yr",
};
const DEFAULT_INTERVAL: BillingInterval = { count: 1, interval: "month" };

function toIntervalUnit(value: string): IntervalUnit | null {
  return INTERVAL_UNITS.find((unit) => unit === value) ?? null;
}

export function formatMoney(amountMajor: number, currencyCode: string): string {
  const fractionDigits = Number.isInteger(amountMajor) ? 0 : 2;
  const formatted = amountMajor.toLocaleString("en-US", {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });
  const code = currencyCode.toUpperCase();

  return code === "USD" ? `$${formatted}` : `${code} ${formatted}`;
}

export function intervalFromRecurring(
  recurring?: { interval: string; interval_count: number } | null,
): BillingInterval {
  const interval = recurring ? toIntervalUnit(recurring.interval) : null;

  if (!recurring || !interval) return DEFAULT_INTERVAL;

  return { count: recurring.interval_count || 1, interval };
}

export function intervalFromBillingCycle(
  billingCycle?: null | string,
): BillingInterval {
  const match = /^(?:(\d+)\s+)?(day|week|month|year)s?$/i.exec(
    (billingCycle ?? "").trim(),
  );
  const interval = match ? toIntervalUnit(match[2]!.toLowerCase()) : null;

  if (!match || !interval) return DEFAULT_INTERVAL;

  return { count: match[1] ? Number(match[1]) : 1, interval };
}

export function intervalLabels({ count, interval }: BillingInterval): {
  renews: string;
  shortUnit: string;
  unit: string;
} {
  if (interval === "month" && count === 3) {
    return { renews: "Renews quarterly", shortUnit: "qtr", unit: "quarter" };
  }
  if (count === 1) {
    return {
      renews: `Renews ${ADVERBS[interval]}`,
      shortUnit: SHORT_UNITS[interval],
      unit: interval,
    };
  }

  return {
    renews: `Renews every ${count} ${interval}s`,
    shortUnit: `${count} ${SHORT_UNITS[interval]}`,
    unit: `${count} ${interval}s`,
  };
}

export function planBenefits(planName: string): string[] {
  const match = /\((\d+) design slots?\)/i.exec(planName);
  const slots = match
    ? [`${match[1]} concurrent design slot${match[1] === "1" ? "" : "s"}`]
    : [];

  return [
    "150+ design capabilities",
    "Unlimited requests & revisions",
    ...slots,
    "Dedicated design team",
  ];
}

export function couponBadgeLabel(
  coupon: AppliedCoupon,
  currencyCode: string,
): null | string {
  if (coupon.percentOff) return `${coupon.percentOff}% OFF`;
  if (coupon.amountOff) {
    return `${formatMoney(coupon.amountOff / 100, coupon.currency ?? currencyCode)} OFF`;
  }

  return null;
}

export function nextChargeDate(
  { count, interval }: BillingInterval,
  from: Date,
): Date {
  const next = new Date(from);

  switch (interval) {
    case "month":
    case "year": {
      const months = interval === "year" ? 12 * count : count;
      const day = next.getDate();

      next.setDate(1);
      next.setMonth(next.getMonth() + months);
      const lastDayOfMonth = new Date(
        next.getFullYear(),
        next.getMonth() + 1,
        0,
      ).getDate();

      next.setDate(Math.min(day, lastDayOfMonth));
      break;
    }
    case "week": {
      next.setDate(next.getDate() + 7 * count);
      break;
    }
    default: {
      next.setDate(next.getDate() + count);
    }
  }

  return next;
}

export function formatChargeDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function cycleMonths({ count, interval }: BillingInterval): number {
  if (interval === "year") return 12 * count;
  if (interval === "month") return count;

  return 0;
}

function ordinal(day: number): string {
  const lastTwo = day % 100;

  if (lastTwo >= 11 && lastTwo <= 13) return `${day}th`;

  const ORDINAL_SUFFIXES: Record<number, string> = {
    1: "st",
    2: "nd",
    3: "rd",
  };

  return `${day}${ORDINAL_SUFFIXES[day % 10] ?? "th"}`;
}

export function isDiscountActiveAtNextCharge(
  coupon: AppliedCoupon,
  interval: BillingInterval,
): boolean {
  if (coupon.duration === "forever") return true;
  if (coupon.duration === "once") return false;

  return (coupon.durationInMonths ?? 0) > cycleMonths(interval);
}

export function thenLine({
  coupon,
  currencyCode,
  discountedMajor,
  fullMajor,
  interval,
  today,
}: {
  coupon: AppliedCoupon | null;
  currencyCode: string;
  discountedMajor: null | number;
  fullMajor: number;
  interval: BillingInterval;
  today: Date;
}): string {
  const { unit } = intervalLabels(interval);
  const full = `${formatMoney(fullMajor, currencyCode)}/${unit}`;
  const when =
    interval.interval === "month" && interval.count === 1
      ? `billed on the ${ordinal(today.getDate())}`
      : `next billed on ${formatChargeDate(nextChargeDate(interval, today))}`;

  if (
    !coupon ||
    discountedMajor === null ||
    !isDiscountActiveAtNextCharge(coupon, interval)
  ) {
    return `Then ${full}, ${when}.`;
  }

  const discounted = `${formatMoney(discountedMajor, currencyCode)}/${unit}`;

  if (coupon.duration === "repeating") {
    const remaining = (coupon.durationInMonths ?? 0) - cycleMonths(interval);

    return `Then ${discounted} for ${remaining} more month${remaining === 1 ? "" : "s"}, then ${full}, ${when}.`;
  }

  return `Then ${discounted}, ${when}.`;
}
